import { FC } from 'react'

import Box, { BoxProps } from '@mui/material/Box'
import Typography, { TypographyProps } from '@mui/material/Typography'
import { styled, useTheme } from '@mui/material/styles'
import { alpha } from '@mui/system'

import { useTranslationWithKey } from 'src/hooks/useTranslationWithKey'

const WrapperBox = styled(Box)<BoxProps>(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  borderRadius: '4px'
}))

type TypeBadgeProps = {
  type: 'module' | 'course' | 'secret' | 'inactive' | 'draft'
  typographyProps?: TypographyProps
}

const translationByTitle = {
  module: 'badge_module_label',
  course: 'badge_course_label',
  secret: 'badge_secret_label',
  inactive: 'badge_inactive_label',
  draft: 'badge_draft_label'
}

const TypeBadge: FC<TypeBadgeProps> = ({ type, typographyProps = {}, ...props }: TypeBadgeProps) => {
  const { sx, ...restProps } = typographyProps
  const theme = useTheme()
  const { t } = useTranslationWithKey('translation', { keyPrefix: 'PAGE.COURSE_OVERVIEW' })

  const isModuleOrCourse = ['module', 'course'].includes(type)

  const badgeStyles = {
    module: {
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.common.white, 0.8)
    },
    course: {
      color: theme.palette.common.white,
      backgroundColor: theme.colorWithOpacity(theme.palette.primary, 48)
    },
    secret: {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.main,
      padding: theme.spacing(1.5, 2.5, 1.5, 2.5)
    },
    inactive: {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.warning.main,
      padding: theme.spacing(1.5, 2.5, 1.5, 2.5)
    },
    draft: {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.secondary.main,
      padding: theme.spacing(1.5, 2.5, 1.5, 2.5)
    }
  }

  const translatedText = isModuleOrCourse ? t(translationByTitle[type]) : t(translationByTitle[type])

  return (
    <WrapperBox style={badgeStyles[type]} {...props}>
      <Typography
        variant={isModuleOrCourse ? 'caption' : 'body2'}
        sx={{ lineHeight: 1, color: 'inherit', ...sx }}
        {...restProps}
      >
        {translatedText}
      </Typography>
    </WrapperBox>
  )
}

export default TypeBadge
